<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import TasksAPI from 'dashboard/api/tasks';

const router = useRouter();
const accountId = useMapGetter('getCurrentAccountId');
const currentUser = useMapGetter('getCurrentUser');
const currentRole = useMapGetter('getCurrentRole');

const drawerOpen = ref(false);
const tasks = ref([]);
// Tareas COMPLETADAS por el agente que esperan TU confirmación (Victor 2026-06-04).
const porConfirmar = ref([]);
// Avisos centrados en pantalla cuando alguien completa una tarea.
const banners = ref([]);
const loading = ref(false);
const filter = ref('todas'); // todas | alta | hoy

const REFRESH_MS = 60000;
let timer = null;

// ¿El usuario actual puede CONFIRMAR esta tarea? (admin/super o quien la asignó)
const isAdmin = computed(() => currentRole.value === 'administrator');
const canConfirm = task =>
  isAdmin.value || task.created_by_id === currentUser.value?.id;

const fetchTasks = async () => {
  if (loading.value) return;
  loading.value = true;
  try {
    const { data: p1 } = await TasksAPI.list({ status: 'pendiente' });
    const { data: p2 } = await TasksAPI.list({ status: 'en_proceso' });
    const { data: p3 } = await TasksAPI.list({ status: 'por_confirmar' });
    const pendientes = p1?.data?.payload || [];
    const enProceso = p2?.data?.payload || [];
    tasks.value = [...pendientes, ...enProceso];
    // Solo las que YO puedo confirmar (las del agente que no me toca confirmar
    // quedan fuera para no llenar la lista).
    porConfirmar.value = (p3?.data?.payload || []).filter(canConfirm);
  } catch (_) { /* silencioso */ } finally { loading.value = false; }
};

const hasConfirmable = computed(() => porConfirmar.value.length > 0);

const count = computed(() => tasks.value.length);
const hasHigh = computed(() =>
  tasks.value.some(t => t.priority === 'alta' || t.priority === 'urgente')
);

// === Conteo de tiempo junto a la campana · Propuesta 2 anillo (Victor 2026-06-03)
// Muestra el tiempo de la tarea MÁS PRÓXIMA A VENCER: días → horas → regresivo
// mm:ss en la última hora. El anillo se vacía con el tiempo. `now` tictac cada 1s.
const now = ref(Date.now());
let tick = null;

const nearestTask = computed(() => {
  const withDue = tasks.value.filter(t => t.due_date);
  if (!withDue.length) return null;
  return withDue.reduce((a, b) => (a.due_date <= b.due_date ? a : b));
});

const countdown = computed(() => {
  const t = nearestTask.value;
  if (!t) return null;
  const dueMs = t.due_date * 1000;
  const remMs = dueMs - now.value;
  const remSec = Math.floor(remMs / 1000);
  // Ventana total (creación → vencimiento) para el llenado del anillo.
  const createdMs = t.created_at ? t.created_at * 1000 : dueMs - 86400000;
  const totalMs = Math.max(dueMs - createdMs, 60000);
  const pct = Math.max(0, Math.min(100, Math.round((remMs / totalMs) * 100)));

  let label;
  let color;
  let pulse = false;
  if (remSec <= 0) {
    label = '¡vencida!';
    color = 'red';
    pulse = true;
  } else if (remSec < 3600) {
    const mm = String(Math.floor(remSec / 60)).padStart(2, '0');
    const ss = String(remSec % 60).padStart(2, '0');
    label = `${mm}:${ss}`;
    color = 'red';
    pulse = true;
  } else if (remSec < 86400) {
    label = `${Math.floor(remSec / 3600)}h`;
    color = 'amber';
  } else {
    const d = Math.floor(remSec / 86400);
    const h = Math.floor((remSec % 86400) / 3600);
    label = h > 0 ? `${d}d ${h}h` : `${d}d`;
    color = 'gold';
  }
  return { label, color, pulse, pct };
});

const RING_CIRC = 94.2; // 2·π·15
const ringColor = computed(
  () =>
    ({ gold: '#c9a35a', amber: '#e0a93c', red: '#e0564b' })[
      countdown.value?.color
    ] || '#c9a35a'
);
const ringDash = computed(
  () => `${((countdown.value?.pct ?? 0) / 100) * RING_CIRC} ${RING_CIRC}`
);
const hasOverdue = computed(() => {
  const now = Date.now() / 1000;
  return tasks.value.some(t => t.due_date && t.due_date < now);
});

const filtered = computed(() => {
  let list = [...tasks.value];
  if (filter.value === 'alta') list = list.filter(t => t.priority === 'alta');
  if (filter.value === 'hoy') {
    const today = new Date(); today.setHours(0,0,0,0);
    const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
    list = list.filter(t => {
      if (!t.due_date) return false;
      const ts = t.due_date * 1000;
      return ts >= today.getTime() && ts < tomorrow.getTime();
    });
  }
  // Orden: alta primero, luego por due_date ASC
  return list.sort((a, b) => {
    if (a.priority !== b.priority) return a.priority === 'alta' ? -1 : 1;
    return (a.due_date || Infinity) - (b.due_date || Infinity);
  });
});

const counts = computed(() => ({
  todas: tasks.value.length,
  alta: tasks.value.filter(t => t.priority === 'alta').length,
  hoy: filtered.value.length, // se recalcula cuando filter='hoy'
}));

// === Agrupado por sucursal + cinta de color (Propuesta C · Victor 2026-06-03)
// Cada sucursal recibe un color estable de la paleta (por branch.id). El panel
// agrupa las tareas bajo un encabezado de sucursal conservando el orden de
// `filtered` (alta primero, luego por vencimiento).
const BRANCH_PALETTE = ['#c9a35a', '#5b8def', '#5bbf8a', '#c77dff', '#e0723f', '#e05b8a', '#4cc4d4'];
const branchColor = task => {
  const id = task.branch?.id;
  if (!id) return '#8b929e';
  return BRANCH_PALETTE[id % BRANCH_PALETTE.length];
};

const grouped = computed(() => {
  const map = new Map();
  filtered.value.forEach(t => {
    const key = t.branch?.id ?? 'none';
    if (!map.has(key)) {
      map.set(key, {
        key,
        name: t.branch?.name || 'Sin sucursal',
        color: branchColor(t),
        tasks: [],
      });
    }
    map.get(key).tasks.push(t);
  });
  return [...map.values()];
});

const goTo = task => {
  if (task.conversation_id && accountId.value) {
    router.push(`/app/accounts/${accountId.value}/conversations/${task.conversation_id}`);
  } else if (accountId.value) {
    router.push(`/app/accounts/${accountId.value}/conversations`);
  }
  drawerOpen.value = false;
};

const markDone = async task => {
  // Tu propia tarea (te la asignaste tú) → hecha directo. Si te la asignó otro,
  // pasa a "por_confirmar" y desaparece de tu lista hasta que el jefe la confirme.
  const selfTask = task.assignee_id === task.created_by_id;
  try {
    await TasksAPI.markStatus(task.id, selfTask ? 'hecha' : 'por_confirmar');
    tasks.value = tasks.value.filter(t => t.id !== task.id);
  } catch (_) {}
};

// El jefe CONFIRMA que la tarea sí se hizo → se da por hecha y desaparece.
const confirmTask = async task => {
  try {
    await TasksAPI.markStatus(task.id, 'hecha');
    porConfirmar.value = porConfirmar.value.filter(t => t.id !== task.id);
    banners.value = banners.value.filter(b => b.id !== task.id);
  } catch (_) {}
};

// El jefe dice que NO estaba hecha → vuelve a pendiente para el agente.
const rejectTask = async task => {
  try {
    await TasksAPI.markStatus(task.id, 'pendiente');
    porConfirmar.value = porConfirmar.value.filter(t => t.id !== task.id);
    banners.value = banners.value.filter(b => b.id !== task.id);
    fetchTasks();
  } catch (_) {}
};

// Aviso en vivo: alguien completó una tarea → banner central + campana verde.
const onTaskCompletedLive = payload => {
  const t = payload?.task;
  if (!t || !canConfirm(t)) return;
  banners.value = [t, ...banners.value.filter(b => b.id !== t.id)].slice(0, 4);
  fetchTasks();
};
const dismissBanner = id => {
  banners.value = banners.value.filter(b => b.id !== id);
};
const openDrawer = () => {
  drawerOpen.value = true;
  banners.value = []; // al abrir Tareas, se limpian los avisos centrados
};

const formatDue = ts => {
  if (!ts) return 'Sin fecha';
  const d = new Date(ts * 1000);
  const today = new Date(); today.setHours(0,0,0,0);
  const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
  const dDay = new Date(d); dDay.setHours(0,0,0,0);
  const fmt = d.toLocaleTimeString('es-DO', { hour: 'numeric', minute: '2-digit', hour12: true });
  if (dDay.getTime() < today.getTime()) return `Vencida (${d.toLocaleDateString('es-DO', { day: 'numeric', month: 'short' })})`;
  if (dDay.getTime() === today.getTime()) return `Hoy ${fmt}`;
  if (dDay.getTime() === tomorrow.getTime()) return `Mañana ${fmt}`;
  return d.toLocaleDateString('es-DO', { day: 'numeric', month: 'short' });
};

const isOverdueTask = task => task.due_date && task.due_date * 1000 < Date.now();

const initials = name =>
  (name || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(p => p[0])
    .join('')
    .toUpperCase() || '–';

onMounted(() => {
  fetchTasks();
  timer = setInterval(fetchTasks, REFRESH_MS);
  tick = setInterval(() => {
    now.value = Date.now();
  }, 1000);
  emitter.on(BUS_EVENTS.TASK_ASSIGNED_LIVE, fetchTasks);
  emitter.on(BUS_EVENTS.TASK_COMPLETED_LIVE, onTaskCompletedLive);
});
onBeforeUnmount(() => {
  if (timer) clearInterval(timer);
  if (tick) clearInterval(tick);
  emitter.off(BUS_EVENTS.TASK_ASSIGNED_LIVE, fetchTasks);
  emitter.off(BUS_EVENTS.TASK_COMPLETED_LIVE, onTaskCompletedLive);
});
</script>

<template>
  <div class="w-full px-1 pt-1 pb-2 flex justify-center">
    <button
      type="button"
      class="relative tb-btn flex flex-col items-center gap-1 py-2 w-full rounded-md transition-colors"
      :class="[
        count === 0 ? 'text-zinc-600 hover:bg-white/3' : '',
        count > 0 && !hasOverdue ? 'tb-active' : '',
        hasOverdue ? 'tb-overdue' : '',
      ]"
      :title="count === 0 ? 'Sin tareas' : `${count} tarea${count===1?'':'s'} pendiente${count===1?'':'s'}`"
      @click="openDrawer"
    >
      <div
        class="relative tb-ringwrap"
        :class="{ 'tb-ring-pulse': countdown && countdown.pulse }"
      >
        <!-- Anillo de progreso (Propuesta 2): se vacía con el tiempo · solo si la
             tarea más próxima tiene fecha de vencimiento. -->
        <svg v-if="countdown" class="tb-ring-svg" viewBox="0 0 36 36">
          <circle class="tb-ring-bg" cx="18" cy="18" r="15" />
          <circle
            class="tb-ring-fg"
            cx="18"
            cy="18"
            r="15"
            :style="{ stroke: ringColor, strokeDasharray: ringDash }"
          />
        </svg>
        <svg
          class="tb-bell"
          :class="count > 0 ? 'tb-wobble' : ''"
          fill="currentColor"
          viewBox="0 0 24 24"
          :style="hasConfirmable ? 'color:#6ee7b7; filter: drop-shadow(0 0 6px rgba(52,211,153,.5));' : ''"
        >
          <path d="M12 22a2 2 0 0 0 2-2h-4a2 2 0 0 0 2 2zm6-6V11c0-3.07-1.64-5.64-4.5-6.32V4a1.5 1.5 0 0 0-3 0v.68C7.64 5.36 6 7.93 6 11v5l-2 2v1h16v-1l-2-2z"/>
        </svg>
        <span
          v-if="count > 0"
          class="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center border-2 border-black tb-badge"
          :class="hasOverdue ? 'tb-badge-red' : 'tb-badge-gold'"
        >{{ count > 99 ? '99+' : count }}</span>
        <!-- Badge verde: tareas completadas esperando TU confirmación -->
        <span
          v-if="hasConfirmable"
          class="absolute -bottom-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-extrabold flex items-center justify-center border-2 border-black"
          style="background: linear-gradient(135deg,#34d399,#059669); color:#04150f;"
          title="Tareas por confirmar"
        >{{ porConfirmar.length > 99 ? '99+' : porConfirmar.length }}</span>
      </div>
      <span
        v-if="countdown"
        class="text-[9px] font-bold leading-none tabular-nums"
        :class="`tb-time-${countdown.color}`"
      >{{ countdown.label }}</span>
      <span class="text-[10px] leading-tight" :style="hasConfirmable ? 'color:#6ee7b7;font-weight:700;' : ''">Tareas</span>
    </button>

    <!-- BANNER CENTRAL: alguien completó una tarea · esperando tu confirmación -->
    <Teleport to="body">
      <div class="fixed top-4 left-1/2 -translate-x-1/2 z-[80] flex flex-col items-center gap-2 pointer-events-none">
        <TransitionGroup
          enter-active-class="transition duration-300 ease-out"
          enter-from-class="opacity-0 -translate-y-3"
          leave-active-class="transition duration-200 ease-in absolute"
          leave-to-class="opacity-0 -translate-y-2"
        >
          <div
            v-for="b in banners"
            :key="`bn-${b.id}`"
            class="pointer-events-auto w-[540px] max-w-[92vw] rounded-2xl overflow-hidden cursor-pointer"
            style="background: linear-gradient(180deg, rgba(30,31,36,.98), rgba(18,19,23,.99)); border:1px solid rgba(52,211,153,.4); box-shadow: 0 16px 48px rgba(0,0,0,.6);"
            @click="openDrawer"
          >
            <div class="p-4 flex items-center gap-3">
              <span class="w-9 h-9 rounded-full grid place-items-center text-white text-[15px] flex-shrink-0" style="background: linear-gradient(135deg,#34d399,#059669);">✓</span>
              <div class="flex-1 min-w-0">
                <div class="text-[10px] font-bold tracking-widest mb-1" style="color:#6ee7b7;">TAREA COMPLETADA · POR CONFIRMAR</div>
                <div class="text-[14px] text-zinc-100 leading-snug">
                  <span class="font-semibold" style="color:#d4b87a;">{{ b.created_by_name || 'Alguien' }}</span>
                  <span class="text-zinc-500"> asignó a </span>
                  <span class="font-semibold text-white">{{ b.assignee_name || '—' }}</span>
                </div>
                <div class="text-[13px] text-zinc-300 mt-0.5 truncate">
                  Tarea: <span class="text-white font-medium">{{ b.title }}</span>
                  <span v-if="b.branch_name" class="text-zinc-500"> · {{ b.branch_name }}</span>
                </div>
              </div>
              <button
                class="text-zinc-600 hover:text-zinc-200 text-base leading-none flex-shrink-0"
                @click.stop="dismissBanner(b.id)"
              >✕</button>
            </div>
          </div>
        </TransitionGroup>
      </div>
    </Teleport>

    <!-- DRAWER -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition-opacity duration-200"
        leave-active-class="transition-opacity duration-150"
        enter-from-class="opacity-0"
        leave-to-class="opacity-0"
      >
        <div
          v-if="drawerOpen"
          class="fixed inset-0 z-[60] bg-black/55"
          @click="drawerOpen = false"
        />
      </Transition>
      <Transition
        enter-active-class="transition-transform duration-250 ease-out"
        leave-active-class="transition-transform duration-150 ease-in"
        enter-from-class="-translate-x-full"
        leave-to-class="-translate-x-full"
      >
        <!-- Panel P1 · Lista ejecutiva (negro + plateado + blanco · Victor 2026-06-03) -->
        <aside
          v-if="drawerOpen"
          class="fixed left-0 top-0 bottom-0 z-[61] w-[372px] max-w-[92vw] flex flex-col shadow-2xl"
          style="background: linear-gradient(180deg, #14161a 0%, #0a0b0d 100%); border-right: 1px solid rgba(255,255,255,.09);"
        >
          <!-- Header -->
          <div class="px-5 py-4 border-b flex items-start justify-between flex-shrink-0" style="border-color: rgba(255,255,255,.07);">
            <div>
              <div class="text-[10px] font-bold tracking-[0.18em]" style="color:#c9ced8;">
                TUS TAREAS PENDIENTES
              </div>
              <div class="text-[13px] font-semibold text-white mt-1.5">
                <span style="color:#c9ced8;">{{ count }}</span> abierta{{ count===1?'':'s' }}
                <span v-if="hasHigh" class="text-zinc-500 font-medium"> · <span style="color:#c9ced8;">{{ counts.alta }}</span> alta{{ counts.alta===1?'':'s' }}</span>
                <span v-if="hasConfirmable" class="font-medium" style="color:#6ee7b7;"> · {{ porConfirmar.length }} por confirmar</span>
              </div>
            </div>
            <button
              class="w-7 h-7 rounded-lg text-zinc-500 hover:text-zinc-200 hover:bg-white/5 flex items-center justify-center text-base leading-none"
              @click="drawerOpen = false"
            >✕</button>
          </div>

          <!-- Filtros -->
          <div class="px-4 py-2.5 border-b flex gap-2 flex-shrink-0" style="border-color: rgba(255,255,255,.05);">
            <button
              v-for="f in ['todas','alta','hoy']"
              :key="f"
              type="button"
              class="text-[11px] font-semibold px-3 py-1.5 rounded-lg transition-colors capitalize"
              :class="filter === f
                ? 'bg-white/[0.08] text-white border border-white/10'
                : 'text-zinc-500 hover:bg-white/5 border border-transparent'"
              @click="filter = f"
            >{{ f }}</button>
          </div>

          <!-- Lista ejecutiva -->
          <div class="flex-1 overflow-y-auto p-2">
            <div v-if="loading && !filtered.length && !porConfirmar.length" class="p-8 text-center text-[11px] text-zinc-500">Cargando…</div>
            <div v-else-if="!filtered.length && !porConfirmar.length" class="p-8 text-center">
              <div class="text-3xl opacity-25 mb-2">✓</div>
              <div class="text-xs text-zinc-400">
                {{ filter === 'todas' ? 'Sin tareas pendientes' : `Ninguna ${filter}` }}
              </div>
            </div>

            <!-- POR CONFIRMAR: el agente la completó, esperando tu OK (Victor 2026-06-04) -->
            <template v-if="porConfirmar.length">
              <div class="flex items-center gap-2 px-2 pt-2 pb-1.5">
                <span class="w-2.5 h-2.5 rounded-full flex-shrink-0" style="background:linear-gradient(135deg,#34d399,#059669);"></span>
                <span class="text-[10px] font-bold tracking-[0.14em] uppercase" style="color:#6ee7b7;">Por confirmar</span>
                <span class="text-[10px] text-zinc-600">· {{ porConfirmar.length }}</span>
              </div>
              <div
                v-for="task in porConfirmar"
                :key="`pc-${task.id}`"
                class="rounded-2xl p-3.5 mb-1.5"
                style="background: linear-gradient(180deg, rgba(16,40,30,.55), rgba(12,24,19,.6)); border:1px solid rgba(52,211,153,.32);"
              >
                <div class="flex items-start gap-3">
                  <div class="relative flex-shrink-0">
                    <div class="w-[38px] h-[38px] rounded-full grid place-items-center text-[13px] font-extrabold overflow-hidden" style="background: linear-gradient(140deg,#dfe3ea,#9aa1ad); color:#11131a;">
                      <img v-if="task.assignee?.avatar_url" :src="task.assignee.avatar_url" class="w-full h-full object-cover" />
                      <template v-else>{{ initials(task.assignee_name || task.assignee?.name) }}</template>
                    </div>
                    <span class="absolute -bottom-1 -right-1 w-5 h-5 rounded-full grid place-items-center text-white text-[10px]" style="background: linear-gradient(135deg,#34d399,#059669);">✓</span>
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="text-[9px] font-bold tracking-widest" style="color:#6ee7b7;">COMPLETADA · ESPERANDO TU CONFIRMACIÓN</div>
                    <div class="text-[14px] text-white font-bold leading-snug mt-1">{{ task.title }}</div>
                    <div class="text-[10.5px] font-semibold mt-1 truncate">
                      <span style="color:#d4b87a;">{{ task.created_by_name || '—' }}</span>
                      <span class="text-zinc-600"> → </span>
                      <span style="color:#c9ced8;">{{ task.assignee_name || 'Sin asignar' }}</span>
                      <span v-if="task.branch_name" class="text-zinc-500"> · {{ task.branch_name }}</span>
                    </div>
                    <div class="flex gap-2 mt-2.5">
                      <button
                        class="text-[11px] px-3.5 py-1.5 rounded-lg font-extrabold"
                        style="background: linear-gradient(180deg,#34d399,#059669); color:#04150f;"
                        @click.stop="confirmTask(task)"
                      >✓ Confirmar hecha</button>
                      <button
                        class="text-[11px] px-3 py-1.5 rounded-lg font-semibold text-zinc-300 border border-white/12 hover:border-white/30 hover:text-white"
                        @click.stop="rejectTask(task)"
                      >↩ No estaba hecha</button>
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <!-- Secciones agrupadas por sucursal (Propuesta C) -->
            <template v-for="group in grouped" :key="group.key">
              <!-- Encabezado de sucursal -->
              <div class="flex items-center gap-2 px-2 pt-3 pb-1.5">
                <span class="w-2.5 h-2.5 rounded-sm flex-shrink-0" :style="{ background: group.color }"></span>
                <span class="text-[10px] font-bold tracking-[0.14em] uppercase" style="color:#c9ced8;">{{ group.name }}</span>
                <span class="text-[10px] text-zinc-600">· {{ group.tasks.length }} tarea{{ group.tasks.length===1?'':'s' }}</span>
              </div>

              <div
                v-for="task in group.tasks"
                :key="task.id"
                class="tb-row flex items-stretch rounded-2xl cursor-pointer overflow-hidden mb-1"
                @click="goTo(task)"
              >
                <!-- Cinta lateral del color de la sucursal -->
                <span class="w-1 flex-shrink-0" :style="{ background: group.color }"></span>

                <div class="flex items-start gap-3 p-3.5 flex-1 min-w-0">
                  <!-- Par de avatares: encargada (dorada, asignó) + receptor (plateado, anillo) -->
                  <div class="relative w-[46px] h-[42px] flex-shrink-0">
                    <!-- Encargada que asignó -->
                    <div
                      class="absolute top-0 left-0 z-10 w-[30px] h-[30px] rounded-full grid place-items-center text-[11px] font-extrabold overflow-hidden"
                      style="background: linear-gradient(140deg,#e4c98a,#b8923f); color:#1a1610; box-shadow: 0 0 0 2px #11131a;"
                      :title="task.created_by ? `Asignó: ${task.created_by.name}` : 'Origen desconocido'"
                    >
                      <img v-if="task.created_by?.avatar_url" :src="task.created_by.avatar_url" class="w-full h-full object-cover" />
                      <template v-else>{{ task.created_by ? initials(task.created_by.name) : '–' }}</template>
                    </div>
                    <!-- Receptor de la tarea -->
                    <div
                      class="absolute bottom-0 right-0 w-[30px] h-[30px] rounded-full grid place-items-center text-[11px] font-extrabold overflow-hidden"
                      :style="task.assignee
                        ? 'background: linear-gradient(140deg,#dfe3ea,#9aa1ad); color:#11131a; box-shadow: 0 0 0 2px #11131a, 0 0 0 3.5px rgba(201,163,90,.5);'
                        : 'background: linear-gradient(140deg,#3a3e46,#23262d); color:#c9ced8; box-shadow: 0 0 0 2px #11131a;'"
                      :title="task.assignee ? `Para: ${task.assignee.name}` : 'Sin asignar'"
                    >
                      <img v-if="task.assignee?.avatar_url" :src="task.assignee.avatar_url" class="w-full h-full object-cover" />
                      <template v-else>{{ task.assignee ? initials(task.assignee.name) : '—' }}</template>
                    </div>
                  </div>

                  <div class="flex-1 min-w-0">
                    <!-- Prioridad -->
                    <span class="inline-flex items-center gap-1.5 text-[9px] font-extrabold tracking-wider uppercase"
                      :class="task.priority === 'alta' ? 'text-white' : 'text-zinc-500'">
                      <span class="w-1.5 h-1.5 rounded-full"
                        :style="task.priority === 'alta' ? 'background:#fff; box-shadow:0 0 8px rgba(255,255,255,.7);' : 'background:#8b929e;'"></span>
                      {{ task.priority === 'alta' ? 'Alta' : 'Normal' }}
                    </span>

                    <!-- Título -->
                    <div class="text-[14px] text-white font-bold leading-snug mt-1.5">{{ task.title }}</div>

                    <!-- Asignó → Receptor -->
                    <div class="text-[10.5px] font-semibold mt-1 truncate flex items-center gap-1">
                      <span style="color:#d4b87a;">{{ task.created_by?.name || '—' }}</span>
                      <span class="text-zinc-600">→</span>
                      <span style="color:#c9ced8;">{{ task.assignee?.name || 'Sin asignar' }}</span>
                    </div>

                    <!-- Cliente -->
                    <div v-if="task.contact?.name" class="text-[11px] text-zinc-500 mt-1 truncate">
                      <span class="text-zinc-300 font-medium">{{ task.contact.name }}</span>
                    </div>

                    <!-- Vencimiento -->
                    <div class="text-[11px] mt-1.5 inline-flex items-center gap-1"
                      :style="isOverdueTask(task) ? 'color:#d4a8a8; font-weight:700;' : 'color:#c9ced8;'">
                      ⏰ {{ formatDue(task.due_date) }}
                    </div>

                    <!-- Acciones (aparecen al pasar el mouse) -->
                    <div class="tb-acts flex gap-2 mt-2.5">
                      <button
                        class="text-[11px] px-3.5 py-1.5 rounded-lg font-extrabold"
                        style="background: linear-gradient(135deg,#eef1f5,#aab1bd); color:#12141a;"
                        @click.stop="goTo(task)"
                      >Empezar</button>
                      <button
                        class="text-[11px] px-3 py-1.5 rounded-lg font-semibold text-zinc-300 border border-white/10 hover:border-white/30 hover:text-white"
                        @click.stop="markDone(task)"
                      >✓ Hecha</button>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>

          <!-- Footer -->
          <div class="px-4 py-3 border-t text-center flex-shrink-0" style="border-color: rgba(255,255,255,.07);">
            <button class="text-[11px] font-bold tracking-wider" style="color:#c9ced8;" @click="fetchTasks">
              ↻ Refrescar
            </button>
          </div>
        </aside>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
.tb-btn { color: rgb(113 113 122); }
.tb-active { color: #c9a35a; background: linear-gradient(180deg, rgba(201,163,90,.08) 0%, rgba(201,163,90,.02) 100%); animation: tb-pulse 1.8s ease-in-out infinite; }
.tb-overdue { color: #fb7185; background: linear-gradient(180deg, rgba(239,68,68,.1) 0%, rgba(239,68,68,.02) 100%); animation: tb-pulse-red 1.4s ease-in-out infinite; }
.tb-badge { animation: tb-badge-bounce 1.4s ease-in-out infinite; }
.tb-badge-gold { background: linear-gradient(135deg, #f1d28a 0%, #d4a84d 100%); color: #1a1610; }
.tb-badge-red  { background: linear-gradient(135deg, #fb7185 0%, #e11d48 100%); color: white; }

/* P1 · fila ejecutiva: hover flotante + acciones reveladas al pasar el mouse */
.tb-row { transition: transform .16s ease, background .16s ease; }
.tb-row + .tb-row { border-top: 1px solid rgba(255,255,255,.05); }
.tb-row:hover { background: rgba(255,255,255,.035); transform: translateY(-2px); }
.tb-acts { opacity: 0; max-height: 0; overflow: hidden; transition: opacity .18s ease, max-height .2s ease; }
.tb-row:hover .tb-acts { opacity: 1; max-height: 50px; }

@keyframes tb-pulse      { 0%,100% { box-shadow: 0 0 0 0 rgba(201,163,90,.45); } 50% { box-shadow: 0 0 0 12px rgba(201,163,90,0); } }
@keyframes tb-pulse-red  { 0%,100% { box-shadow: 0 0 0 0 rgba(239,68,68,.55); } 50% { box-shadow: 0 0 0 14px rgba(239,68,68,0); } }
@keyframes tb-badge-bounce { 0%,100% { transform: scale(1); } 50% { transform: scale(1.18); } }
@keyframes tb-wobble     { 0% { transform: rotate(0); } 5% { transform: rotate(-18deg); } 12% { transform: rotate(14deg); } 20% { transform: rotate(-10deg); } 28% { transform: rotate(7deg); } 35% { transform: rotate(-4deg); } 42%, 100% { transform: rotate(0); } }
.tb-wobble { animation: tb-wobble 3.5s ease-in-out infinite; transform-origin: top center; }
.tb-pulse-red { animation: tb-badge-bounce 1.2s ease-in-out infinite; }

/* === Anillo de progreso junto a la campana (Propuesta 2 · Victor 2026-06-03) === */
.tb-ringwrap { width: 36px; height: 36px; display: grid; place-items: center; border-radius: 50%; }
.tb-ring-svg { position: absolute; inset: 0; width: 36px; height: 36px; transform: rotate(-90deg); }
.tb-ring-bg { fill: none; stroke: rgba(255,255,255,.09); stroke-width: 3; }
.tb-ring-fg { fill: none; stroke-width: 3; stroke-linecap: round; transition: stroke-dasharray .6s linear; }
.tb-bell { width: 20px; height: 20px; position: relative; z-index: 1; }
.tb-ring-pulse { animation: tb-ring-pulse 1.1s ease-in-out infinite; }
@keyframes tb-ring-pulse { 0%,100% { box-shadow: 0 0 0 0 rgba(224,86,75,.55); } 50% { box-shadow: 0 0 0 6px rgba(224,86,75,0); } }
.tb-time-gold  { color: #e7cf9a; }
.tb-time-amber { color: #f2cd7e; }
.tb-time-red   { color: #ec7066; }
</style>
